import React, { useState, useContext, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Mail, Building2, Shield, Loader2, ChevronDown, Info, MapPin, PlusCircle, Building, AlertCircle } from 'lucide-react';
import Tooltip from '../Shared/Tooltip';
import { AuthContext } from '../../contexts/AuthContext';
import userService from '../../services/userService';
import siteService from '../../services/siteService';
import toast from 'react-hot-toast';

const AddUserModal = ({ isOpen, onClose, onSuccess, sponsorId, studyId, siteId = null, availableRoles = [] }) => {
    const { user } = useContext(AuthContext);
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        role: '',
        is_internal: false,
        sponsor_id: sponsorId,
        study_id: studyId,
        site_ids: siteId ? [siteId] : []
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [availableSites, setAvailableSites] = useState([]);
    const [loadingSites, setLoadingSites] = useState(false);
    const [isFocused, setIsFocused] = useState({
        email: false,
        firstName: false,
        lastName: false,
        role: false
    });

    useEffect(() => {
        setFormData(prevData => ({ 
            ...prevData, 
            sponsor_id: sponsorId 
        }));
    }, [sponsorId]);

    useEffect(() => {
        setFormData(prevData => ({ 
            ...prevData, 
            study_id: studyId,
            site_ids: siteId ? [siteId] : prevData.site_ids
        }));
    }, [studyId, siteId]);

    // Fetch available sites when studyId is provided and modal is open
    useEffect(() => {
        const fetchSites = async () => {
            if (studyId && isOpen && !siteId) { // Only fetch if we're in study-level mode
                setLoadingSites(true);
                try {
                    const sites = await siteService.getSitesForStudy(studyId);
                    setAvailableSites(sites);
                } catch (error) {
                    console.error('Error fetching sites:', error);
                    setAvailableSites([]);
                } finally {
                    setLoadingSites(false);
                }
            }
        };
        fetchSites();
    }, [studyId, isOpen, siteId]);

    // Required field marker component
    const RequiredFieldMarker = () => (
        <span className="ml-0.5 text-danger-500" aria-label="required field">*</span>
    );

    const closeDialog = () => {
        // reset form data
        setFormData({
            email: '',
            firstName: '',
            lastName: '',
            role: '',
            is_internal: false,
            sponsor_id: formData.sponsor_id,
            study_id: formData.study_id,
            site_ids: siteId ? [siteId] : []
        });
        
        // Reset focus states
        setIsFocused({
            email: false,
            firstName: false,
            lastName: false,
            role: false
        });
        
        setError('');
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Require at least one site for site-scoped roles when adding at study level
        if (studyId && !siteId && roleRequiresSiteSelection && formData.site_ids.length === 0) {
            setError('Please select at least one site for this role. Study Coordinators and Investigators must be assigned to specific sites.');
            setLoading(false);
            return;
        }

        try {
            // Debug logging to see what's being sent
            console.log('[ADD_USER_MODAL] Form data being submitted:', formData);
            const response = await userService.addUser(formData);
            onSuccess();
            closeDialog();
            
            toast.success(`User ${formData.email} was created successfully`, {
                style: {
                    borderRadius: '10px',
                    background: '#10B981',
                    color: '#fff',
                }
            });
            
        } catch (error) {
            console.error('Error adding user:', error);
            console.log('Error response data:', error.response?.data);
            console.log('Error response status:', error.response?.status);
            
            // Extract error message with multiple fallbacks
            let errorMessage = error.response?.data?.__all__?.[0] || 
                              error.response?.data?.detail ||
                              error.response?.data?.error ||
                              error.message ||
                              'Failed to create user';
            
            console.log('Extracted error message:', errorMessage);
            
            // Handle different error message formats
            let finalErrorMessage = errorMessage;
            
            // Check if it's the email already registered error
            if (errorMessage && (errorMessage.includes('Email is already registered') || errorMessage.includes('already registered'))) {
                // Try to extract email from different formats
                let email = formData.email; // fallback to form email
                
                if (errorMessage.includes(': ')) {
                    const parts = errorMessage.split(': ');
                    if (parts.length > 1) {
                        email = parts[parts.length - 1]; // get the last part after colon
                    }
                }
                
                finalErrorMessage = `This email address (${email}) is already registered in the system. Please use a different email address or contact an administrator if you believe this is an error.`;
                console.log('Updated user-friendly message:', finalErrorMessage);
            }
            
            errorMessage = finalErrorMessage;
            
            setError(errorMessage);
            
            toast.error(errorMessage, {
                style: {
                    borderRadius: '10px',
                    background: '#EF4444',
                    color: '#fff',
                }
            });
            
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSiteSelection = (siteId) => {
        setFormData(prevData => {
            const newSiteIds = prevData.site_ids.includes(siteId)
                ? prevData.site_ids.filter(id => id !== siteId)
                : [...prevData.site_ids, siteId];
            return {
                ...prevData,
                site_ids: newSiteIds
            };
        });
    };

    const handleSelectAllSites = () => {
        const allSiteIds = availableSites.map(site => site.id);
        const isAllSelected = allSiteIds.every(id => formData.site_ids.includes(id));
        
        setFormData(prevData => ({
            ...prevData,
            site_ids: isAllSelected ? [] : allSiteIds
        }));
    };

    // Check if role requires site selection
    const roleRequiresSiteSelection = ['investigator', 'study_coordinator'].includes(formData.role);

    const handleFocus = (field) => {
        setIsFocused(prev => ({
            ...prev,
            [field]: true
        }));
    };

    const handleBlur = (field) => {
        setIsFocused(prev => ({
            ...prev,
            [field]: false
        }));
    };

    // Animation variants
    const overlayVariants = {
        hidden: { opacity: 0 },
        visible: { 
            opacity: 1,
            transition: { duration: 0.2 }
        },
        exit: { 
            opacity: 0,
            transition: { duration: 0.2 }
        }
    };

    const modalVariants = {
        hidden: { 
            opacity: 0, 
            y: 8
        },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: {
                duration: 0.2,
                ease: 'easeOut'
            }
        },
        exit: {
            opacity: 0,
            y: 8,
            transition: {
                duration: 0.15
            }
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <Dialog
                    as={motion.div}
                    static
                    open={isOpen}
                    onClose={closeDialog}
                    className="relative z-50"
                >
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={overlayVariants}
                        className="fixed inset-0 bg-gray-950/40 dark:bg-black/70"
                        aria-hidden="true"
                    />
                    
                    <div className="fixed inset-0 flex items-center justify-center p-4 sm:p-6">
                        <Dialog.Panel
                            as={motion.div}
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="mx-auto flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10"
                        >
                            {/* Header */}
                            <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-5">
                                <Dialog.Title className="flex items-center gap-2.5 text-base font-semibold text-gray-900 dark:text-gray-50">
                                    <PlusCircle className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
                                    Add New User
                                </Dialog.Title>
                                <button
                                    onClick={closeDialog}
                                    className="-mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                                    aria-label="Close"
                                >
                                    <X className="h-[18px] w-[18px]" />
                                </button>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                            <div className="flex-1 overflow-y-auto px-6 pb-6 pt-3">
                                {/* Info notice */}
                                <div className="mb-5 flex items-start gap-2.5 rounded-lg border border-gray-200/70 bg-gray-50 px-3.5 py-3 text-xs leading-relaxed text-gray-600 dark:border-white/[0.07] dark:bg-white/[0.03] dark:text-gray-400">
                                    <Info className="mt-px h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                                    <p>
                                        An authentication link will be emailed to the user. They can click the link to set their password and activate their account.
                                    </p>
                                </div>
                                
                                {/* Error message */}
                                {error && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: -8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.2, ease: 'easeOut' }}
                                        className="mb-5 flex items-start gap-2.5 rounded-lg border border-danger-500/25 bg-danger-50 px-3.5 py-3 text-sm text-danger-700 dark:bg-danger-500/10 dark:text-danger-400"
                                    >
                                        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                                        <p>{error}</p>
                                    </motion.div>
                                )}
                                
                                <div className="space-y-5">
                                    {/* Email field */}
                                    <div>
                                        <label className="label flex items-center">
                                            Email<RequiredFieldMarker />
                                        </label>
                                        <div className="relative group">
                                            <Mail className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transition-colors ${
                                                isFocused.email ? 'text-primary-500' : 'text-gray-400'
                                            }`} />
                                            <input
                                                type="email"
                                                name="email"
                                                required
                                                value={formData.email}
                                                onChange={handleChange}
                                                onFocus={() => handleFocus('email')}
                                                onBlur={() => handleBlur('email')}
                                                className="input pl-10"
                                                placeholder="user@example.com"
                                            />
                                        </div>
                                    </div>

                                    {/* Name fields */}
                                    <div className="grid grid-cols-2 gap-4">
                                        {/* First name */}
                                        <div>
                                            <label className="label flex items-center">
                                                First Name<RequiredFieldMarker />
                                            </label>
                                            <div className="relative group">
                                                <User className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transition-colors ${
                                                    isFocused.firstName ? 'text-primary-500' : 'text-gray-400'
                                                }`} />
                                                <input
                                                    type="text"
                                                    name="firstName"
                                                    required
                                                    value={formData.firstName}
                                                    onChange={handleChange}
                                                    onFocus={() => handleFocus('firstName')}
                                                    onBlur={() => handleBlur('firstName')}
                                                    className="input pl-10"
                                                    placeholder="First name"
                                                />
                                            </div>
                                        </div>

                                        {/* Last name */}
                                        <div>
                                            <label className="label flex items-center">
                                                Last Name<RequiredFieldMarker />
                                            </label>
                                            <div className="relative group">
                                                <input
                                                    type="text"
                                                    name="lastName"
                                                    required
                                                    value={formData.lastName}
                                                    onChange={handleChange}
                                                    onFocus={() => handleFocus('lastName')}
                                                    onBlur={() => handleBlur('lastName')}
                                                    className="input"
                                                    placeholder="Last name"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Role field */}
                                    <div>
                                        <label className="label flex items-center">
                                            Role<RequiredFieldMarker />
                                        </label>
                                        <div className="relative group">
                                            <Building className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transition-colors ${
                                                isFocused.role ? 'text-primary-500' : 'text-gray-400'
                                            }`} />
                                            <select
                                                name="role"
                                                required
                                                value={formData.role}
                                                onChange={handleChange}
                                                onFocus={() => handleFocus('role')}
                                                onBlur={() => handleBlur('role')}
                                                className="input appearance-none pl-10 pr-9"
                                            >
                                                <option value="">Select a role</option>
                                                {availableRoles && availableRoles.length > 0 ? (
                                                    availableRoles.map((role) => (
                                                        <option key={role.value} value={role.value}>
                                                            {role.label}
                                                        </option>
                                                    ))
                                                ) : (
                                                    userService.getRoles(user).map((role) => (
                                                        <option key={role.value} value={role.value}>
                                                            {role.label}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                                                <ChevronDown className="h-4 w-4" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Site Selection - only show when in study-level mode (not single site) and role requires site selection */}
                                    {studyId && !siteId && roleRequiresSiteSelection && (
                                        <div>
                                            <label className="label flex items-center">
                                                Site Access
                                                <Tooltip 
                                                    content="Select one or more sites this user should have access to. Investigators and Study Coordinators typically have access to individual sites."
                                                    position="bottom"
                                                    iconClassName="ml-2 h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help"
                                                />
                                            </label>
                                            {availableSites.length > 0 && (
                                                <div className="flex justify-between items-center mb-2">
                                                    <button
                                                        type="button"
                                                        onClick={handleSelectAllSites}
                                                        className="link text-sm"
                                                    >
                                                        {availableSites.every(site => formData.site_ids.includes(site.id)) ? 'Deselect All' : 'Select All'}
                                                    </button>
                                                </div>
                                            )}
                                            <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-white/10 dark:bg-white/[0.03]">
                                                {loadingSites ? (
                                                    <div className="flex items-center justify-center py-4">
                                                        <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />
                                                        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading sites...</span>
                                                    </div>
                                                ) : availableSites.length === 0 ? (
                                                    <div className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                                        No sites available for this study
                                                    </div>
                                                ) : (
                                                    availableSites.map((site) => (
                                                        <div key={site.id} className="flex items-center gap-3">
                                                            <input
                                                                type="checkbox"
                                                                id={`site-${site.id}`}
                                                                checked={formData.site_ids.includes(site.id)}
                                                                onChange={() => handleSiteSelection(site.id)}
                                                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-gray-600"
                                                            />
                                                            <label 
                                                                htmlFor={`site-${site.id}`} 
                                                                className="flex flex-1 cursor-pointer items-center text-sm text-gray-700 dark:text-gray-200"
                                                            >
                                                                <MapPin className="mr-2 h-4 w-4 text-gray-400" />
                                                                {site.site_name}
                                                            </label>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                            <p className="hint">
                                                {siteId ? "This user will be added to the current site only." : "Select one or more sites this user should have access to. Investigators and Study Coordinators typically have access to individual sites."}
                                            </p>
                                        </div>
                                    )}

                                    {/* Single Site Mode Info */}
                                    {siteId && (
                                        <div className="rounded-lg border border-gray-200/70 bg-gray-50 px-3.5 py-3 dark:border-white/[0.07] dark:bg-white/[0.03]">
                                            <div className="flex items-start gap-2.5">
                                                <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                                                <div>
                                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Adding User to Specific Site</h4>
                                                    <p className="mt-0.5 text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                                                        This user will be granted access to this site only. They will automatically be assigned to the study as well.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Study Team Internal/External Checkbox - only show when Study Team is selected */}
                                    {formData.role === 'study_team' && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="space-y-3"
                                        >
                                            <div className="option-row justify-start gap-3">
                                                <input
                                                    type="checkbox"
                                                    id="is_internal"
                                                    name="is_internal"
                                                    checked={formData.is_internal}
                                                    onChange={handleChange}
                                                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-gray-600"
                                                />
                                                <div className="flex-1">
                                                    <label htmlFor="is_internal" className="block cursor-pointer text-sm font-medium text-gray-900 dark:text-gray-100">
                                                        Internal Study Team Member
                                                    </label>
                                                    <p className="mt-0.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                                                        Check this box if the Study Team member is an internal employee who requires multi-factor authentication (MFA). Leave unchecked for external users.
                                                    </p>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </div>
                            </div>

                                {/* Submit button */}
                                <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="btn btn-primary"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                <span>Adding...</span>
                                            </>
                                        ) : (
                                            'Add User'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </Dialog.Panel>
                    </div>
                </Dialog>
            )}
        </AnimatePresence>
    );
};

export default AddUserModal;